import { Building2, Save, Upload, X } from "lucide-react";
import React, { useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { httpService } from "../services/httpService";
import { Provider, ProviderTier } from "../types/provider";
import ProviderTierSelector from "./admin/ProviderTierSelector";

interface ProviderCreateWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onProviderCreated: (provider: Provider) => void;
  onRefreshProviders: () => Promise<void>;
}

const ProviderCreateWizard: React.FC<ProviderCreateWizardProps> = ({ 
  isOpen, 
  onClose, 
  onProviderCreated,
  onRefreshProviders
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  
  const [currentStep, setCurrentStep] = useState<"name" | "logo">("name");
  const [name, setName] = useState("");
  const [tier, setTier] = useState<ProviderTier | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdProvider, setCreatedProvider] = useState<Provider | null>(null);

  const resetWizard = () => {
    setCurrentStep("name");
    setName("");
    setTier(null);
    setLogoFile(null);
    setLogoPreview(null);
    setLoading(false);
    setError(null);
    setCreatedProvider(null);
  };

  const handleClose = () => {
    resetWizard();
    onClose();
  };

  const handleNameNext = async () => {
    if (!name.trim()) {
      setError("Please enter a provider name");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Tier is optional at creation: omit `data` entirely when no tier is
      // chosen so the backend leaves the column unset (PWA-2335).
      const body: { name: string; data?: { tier: ProviderTier } } = { name: name.trim() };
      if (tier) body.data = { tier };

      const response = await httpService.post("/providers", body);
      const provider = response.data.provider;
      
      setCreatedProvider(provider);
      setCurrentStep("logo");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create provider");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file size must be less than 10MB');
        return;
      }

      setLogoFile(file);
      setError(null);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async (providerId: string) => {
    if (!logoFile) return;

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await httpService.post(`/providers/${providerId}/logo`, formData, {
        timeout: 60000 // 60 seconds for file uploads
      });
      
      // Update the created provider with the logo URL
      if (response.data.logo?.publicUrl && createdProvider) {
        setCreatedProvider({
          ...createdProvider,
          logoUrl: response.data.logo.publicUrl
        });
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      throw new Error('Failed to upload logo. Please try again.');
    }
  };

  const handleFinish = async () => {
    if (!createdProvider) return;

    try {
      setLoading(true);
      setError(null);
      
      // Upload logo if selected
      if (logoFile) {
        await uploadLogo(createdProvider.id);
      }
      
      // Refresh providers list and close the wizard
      await onRefreshProviders();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to complete setup");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
          <div className="flex items-center">
            <Building2 className={`w-6 h-6 mr-3 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
            <h2 className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
              Create New Provider
            </h2>
          </div>
          <button
            onClick={handleClose}
            className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step indicator */}
          <div className="mb-6">
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Step {currentStep === 'name' ? '1' : '2'} of 2
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${isDarkMode ? "bg-blue-400" : "bg-blue-600"}`}
                style={{ width: currentStep === 'name' ? '50%' : '100%' }}
              ></div>
            </div>
          </div>

          {error && (
            <div className={`rounded-lg p-3 mb-4 ${
              isDarkMode ? "bg-red-900/30 border border-red-700" : "bg-red-50 border border-red-200"
            }`}>
              <p className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-600"}`}>{error}</p>
            </div>
          )}

          {/* Step 1: Name */}
          {currentStep === "name" && (
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Provider Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDarkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "border-gray-300"
                  }`}
                  placeholder="Investment Provider Name"
                  autoFocus
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Tier (Optional)
                </label>
                <ProviderTierSelector value={tier} onChange={setTier} />
              </div>
            </div>
          )}

          {/* Step 2: Logo */}
          {currentStep === "logo" && (
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Provider Logo (Optional)
                </label>
                
                {/* Logo Preview */}
                {logoPreview && (
                  <div className="mb-3">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                  </div>
                )}

                {/* File Input */}
                <div className="flex items-center space-x-3">
                  <label
                    className={`flex items-center px-4 py-2 border border-dashed rounded-lg cursor-pointer transition-colors ${
                      isDarkMode
                        ? "border-gray-600 hover:border-gray-500 hover:bg-gray-700/50"
                        : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                    }`}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    <span className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                      {logoPreview ? "Change Logo" : "Upload Logo"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </label>
                  
                  {logoFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      className={`text-sm ${isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-700"}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}>
                  Supported formats: PNG, JPG, JPEG, GIF, WebP. Max size: 10MB.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                isDarkMode
                  ? "text-gray-300 hover:text-white hover:bg-gray-700"
                  : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              Cancel
            </button>
            
            <div className="flex space-x-3">
              {currentStep === "name" ? (
                <button
                  onClick={handleNameNext}
                  disabled={loading || !name.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Next
                </button>
              ) : (
                <button
                  onClick={handleFinish}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Finish
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderCreateWizard;
